import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack,
  StackDivider,
  HStack,
  useDisclosure
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import useDebounce from '@hooks/useDebounce';
import useDidMountEffect from '@hooks/useDidMount';
import {
  caseInvestigationManagerConfig
} from '@views/CaseInvestigationManager/utils';
import Pagination from '@components/Pagination';
import SearchBox from '@components/SearchBox/SearchBox';
import FilterSelect from '@components/FiltersBox/FilterSelect';
import { updateFalconCaseInvestigationManagerReqData } from '@store/actions/reqDataActions';
import { CARD_TYPE, ROLES, SORT_ORDER } from '@constants';
import { CASE_INVESTIGATION_DEFAULT_FILTERS } from '@views/CaseInvestigationManager/constant';
import GenericCard from '@components/GenericCard';
import { FaArrowRightLong } from 'react-icons/fa6';
import { MdAdd } from 'react-icons/md';
import useAuthRole from '@hooks/useAuthRole';
import ModalLayout from '@components/Modal/ModalLayout';
import CreateCaseInvestigationModal from '@components/Modal/CreateCaseInvestigationModal';
import ContentLayout from '@layouts/ContentLayout';


const CaseInvestigationManager = () => {
  const { hasAnyAuthority } = useAuthRole();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    data: configData,
    isLoading: isConfigLoading,
    error: configError
  } = useSelector((state) => state.caseInvestigationManager.config);

  const searches = configData?.searches || [];
  const filters = configData?.filters || [];
  const sortKey = configData?.sorting?.defaultSortKey || 'created_at';
  const sortOrder = configData?.sorting?.defaultsortOrder || SORT_ORDER.DESC;

  // Redux States
  const reqData = useSelector(
    (state) => state.reqData.caseInvestigationManager?.falcon || {}
  );

  const { data, isLoading, error } = useSelector(
    (state) =>
      state.caseInvestigationManager?.falcon?.caseInvestigations || {
        data: {},
        isLoading: false,
        error: null
      }
  );

  const { result: caseInvestigations, totalPage } = data || {};

  // Request data with proper defaults
  const {
    pageNumber = 0,
    searchKey = '',
    searchValue = '',
    filters: reqFilters = {}
  } = reqData;

  // Local state with proper initialization
  const [filterParams, setFilterParams] = useState(reqFilters || {});
  const [searchParams, setSearchParams] = useState({
    searchKey: searchKey || '',
    searchValue: searchValue || ''
  });
  const [sortParams, setSortParams] = useState({
    sortKey,
    sortOrder
  });
  const [isModalLoading, setIsModalLoading] = useState(false);

  const debouncedFilterParams = useDebounce(filterParams, 500);
  const debouncedSearchParams = useDebounce(searchParams, 500);
  const debouncedSortParams = useDebounce(sortParams, 500);

  const {
    isOpen: isCreateNewCaseInvestigationModalOpen,
    onOpen: onCreateNewCaseInvestigationModalOpen,
    onClose: onCreateNewCaseInvestigationModalClose
  } = useDisclosure();

  useEffect(() => {
    if (!configData && !isConfigLoading && !configError) {
      fetchDashboardConfig();
    }
  }, [dispatch, configData, isConfigLoading, configError]);

  const fetchDashboardConfig = useCallback(() => {
    dispatch(
      caseInvestigationManagerConfig.actions.getDashboardConfig()
    );
  }, [dispatch]);

  const fetchFalconCaseInvestigations = useCallback(() => {
    if (!reqData || !configData) {
      return;
    }
    dispatch(
      caseInvestigationManagerConfig.actions.getAllCaseInvestigations({
        payload: {
          ...reqData,
          filters: debouncedFilterParams,
          searchKey: debouncedSearchParams.searchKey,
          searchValue: debouncedSearchParams.searchValue,
          sortKey: debouncedSortParams.sortKey,
          sortOrder: debouncedSortParams.sortOrder
        }
      })
    );
  }, [
    dispatch,
    configData,
    debouncedFilterParams,
    debouncedSearchParams,
    debouncedSortParams
  ]);

  useDidMountEffect(() => {
    fetchFalconCaseInvestigations();
  }, [
    debouncedFilterParams,
    debouncedSearchParams,
    debouncedSortParams,
    pageNumber,
    dispatch
  ]);

  const handleRetryConfig = useCallback(() => {
    fetchDashboardConfig();
  }, [fetchDashboardConfig]);

  const handleSortChange = useCallback(
    (field, value) => {
      setSortParams((prev) => ({
        ...prev,
        sortOrder: value
      }));

      dispatch(
        updateFalconCaseInvestigationManagerReqData({
          ...reqData,
          sortOrder: value,
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  const handlePageChange = useCallback(
    (page) => {
      const newPageNumber = Math.max(0, page - 1);
      dispatch(
        updateFalconCaseInvestigationManagerReqData({
          ...reqData,
          pageNumber: newPageNumber
        })
      );
    },
    [dispatch, reqData]
  );

  const handleSearchChange = useCallback(
    (search) => {
      const newSearchParams = {
        searchKey: search.searchKey || '',
        searchValue: search.searchValue || ''
      };

      setSearchParams(newSearchParams);

      dispatch(
        updateFalconCaseInvestigationManagerReqData({
          ...reqData,
          searchKey: newSearchParams.searchKey,
          searchValue: newSearchParams.searchValue,
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  const handleFilterChange = useCallback(
    ({ key, filters: filterValues }) => {
      setFilterParams((prev) => {
        const updatedParams = { ...prev };
        if (!filterValues || filterValues.length === 0) {
          delete updatedParams[key];
        } else {
          updatedParams[key] = filterValues;
        }
        return updatedParams;
      });

      dispatch(
        updateFalconCaseInvestigationManagerReqData({
          ...reqData,
          filters: {
            ...reqData.filters,
            [key]: filterValues || []
          },
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  const handleDetailClick = (caseId) => {
    navigate(
      `/case-investigation-manager/case/${caseId}`
    );
  };

  const handleCreateCaseInvestigationSubmit = (formData) => {
    dispatch(
      caseInvestigationManagerConfig.actions.createNewCaseInvestigation({
        payload: formData,
        onSuccess: () => {
          dispatch(
            caseInvestigationManagerConfig.actions.getAllCaseInvestigations({
              payload: reqData
            })
          );
        },
        onComplete: () => {
          setIsModalLoading(false);
        }
      })
    );
  };

  // Render functions
  const renderConfigLoadingState = () => (
    <Box
      p={6}
      borderRadius="xl"
      w="full"
      bg="backGround"
      borderWidth="1px"
      borderColor="whiteAlpha.300"
    >
      <Flex justifyContent="center" alignItems="center" w="full" h="200px">
        <VStack spacing={4}>
          <Spinner thickness="4px" size="lg" color="colorPrimary" />
          <Text color="whiteAlpha.600" fontSize="sm">
            Loading dashboard configuration...
          </Text>
        </VStack>
      </Flex>
    </Box>
  );

  const renderConfigErrorState = () => (
    <Box
      p={6}
      bg="backGround"
      border="1px solid"
      borderColor="red.300"
      borderRadius="xl"
    >
      <VStack spacing={4} align="stretch">
        <Text color="red.500" fontSize="sm" fontWeight="medium">
          Failed to load dashboard configuration
        </Text>
        <Text color="red.400" fontSize="xs">
          {configError || 'An unknown error occurred'}
        </Text>
        <Button
          size="sm"
          colorScheme="red"
          variant="outline"
          onClick={handleRetryConfig}
          alignSelf="flex-start"
        >
          Retry
        </Button>
      </VStack>
    </Box>
  );

  const renderLoadingState = () => (
    <Flex justify="center" align="center" w="full" h="200px">
      <VStack spacing={3}>
        <Spinner thickness="4px" size="lg" color="colorPrimary" />
        <Text fontSize="sm" color="whiteAlpha.600">
          Loading case investigations...
        </Text>
      </VStack>
    </Flex>
  );

  // Render error state
  const renderErrorState = () => (
    <Box
      p={4}
      bg="backGround"
      border="1px solid"
      borderColor="red.300"
      borderRadius="xl"
    >
      <Text color="red.500" fontSize="sm">
        Error loading case investigations: {error}
      </Text>
    </Box>
  );

  // Render empty state
  const renderEmptyState = () => (
    <Box
      p={8}
      bg="backGround"
      border="1px solid"
      borderColor="whiteAlpha.300"
      borderRadius="xl"
      textAlign="center"
    >
      <Text color="whiteAlpha.700" fontSize="lg">
        No Case Investigations Found!
      </Text>
    </Box>
  );

  const renderCaseInvestigationList = () => (
    <VStack spacing={4} align="stretch">
      {caseInvestigations.map((caseInvestigationData) => (
        <GenericCard
          key={caseInvestigationData?.caseId}
          cardData={caseInvestigationData}
          cardType={
            CARD_TYPE.CASE_INVESTIGATION_MANAGER.CASE_INVESTIGATION_CARD
          }
          maxW={'100%'}
          ActionButtons={[
            <Button
              key="details-button"
              colorScheme="brand"
              color="black"
              width="8rem"
              size="sm"
              fontSize="sm"
              mt={3}
              onClick={() => handleDetailClick(caseInvestigationData?.caseId)}
              rightIcon={<FaArrowRightLong />}
            >
              Details
            </Button>
          ]}
        />
      ))}
    </VStack>
  );

  // Main render function
  const renderContent = () => {
    if (isLoading) return renderLoadingState();
    if (error) return renderErrorState();
    if (!caseInvestigations?.length) return renderEmptyState();
    return renderCaseInvestigationList();
  };

  const renderModals = () => {
    return (
      <ModalLayout
        size={'2xl'}
        isOpen={isCreateNewCaseInvestigationModalOpen}
        onClose={onCreateNewCaseInvestigationModalClose}
      >
        <CreateCaseInvestigationModal
          title={'Create Case Investigation'}
          onClose={onCreateNewCaseInvestigationModalClose}
          fieldsGroup={caseInvestigationManagerConfig.actions.fieldsGroupBuilder()}
          onSubmit={handleCreateCaseInvestigationSubmit}
          isLoading={isModalLoading}
        />
      </ModalLayout>
    );
  };

  const renderMainContent = () => {
    if (isConfigLoading) return renderConfigLoadingState();
    if (configError) return renderConfigErrorState();

    const hasValidConfig =
      configData &&
      (searches?.length > 0 ||
        filters?.length > 0 ||
        typeof filters === 'object');

    return (
      <Box h="100%" overflow="hidden">
        {renderModals()}
        <VStack
          h="100%"
          overflowY="auto"
          overflowX="hidden"
          alignItems="stretch"
          gap={4}
          pb={4}
          divider={<StackDivider borderColor="whiteAlpha.300" />}
        >
          {/* Search & Filters - Only show when config is available */}
          {hasValidConfig && (
            <VStack
              justifyContent="flex-start"
              alignItems="start"
              alignSelf="flex-start"
              gap={2}
            >
              {/* Search Box */}
              {Array.isArray(searches) && searches.length > 0 && (
                <SearchBox
                  searches={searches}
                  handleSearchChange={handleSearchChange}
                />
              )}

              {/* Filter Select */}
              {((Array.isArray(filters) && filters.length > 0) ||
                (typeof filters === 'object' &&
                  filters !== null &&
                  Object.keys(filters).length > 0)) && (
                <FilterSelect
                  handleFilterSubmit={handleFilterChange}
                  filtersData={filters}
                  defaultFiltersData={CASE_INVESTIGATION_DEFAULT_FILTERS}
                />
              )}
            </VStack>
          )}

          {/* Pagination */}
          {!isLoading && !error && totalPage > 1 && (
            <Pagination
              onPageChange={handlePageChange}
              currentPage={pageNumber + 1}
              totalPages={totalPage}
            />
          )}

          {/* Content */}
          {renderContent()}
        </VStack>
      </Box>
    );
  };

  return (
    <ContentLayout>
      <VStack
        overflowY="hidden"
        overflowX="hidden"
        h={{ lg: '90vh' }}
        pt={10}
        pb={4}
        px={{ base: 4, sm: 6, lg: 10 }}
        alignItems="stretch"
        gap={4}
      >
        <HStack justify={'space-between'}>
          <Heading size="lg" color="colorPrimary">
            Case Investigation Manager
          </Heading>
          {hasAnyAuthority(
            ROLES.ADMIN_ROLE,
            ROLES.FALCON_ADMIN,
            ROLES.FALCON_OPS_MANAGER
          ) && (
            <Button
              colorScheme={'brand'}
              size="sm"
              bg="backGround"
              border={'2px solid'}
              borderColor={'whiteAlpha.500'}
              borderRadius={'lg'}
              p={4}
              onClick={onCreateNewCaseInvestigationModalOpen}
              leftIcon={<MdAdd size={'1.5rem'} />}
            >
              Create Case
            </Button>
          )}
        </HStack>

        {renderMainContent()}
      </VStack>
    </ContentLayout>
  );
};

export default CaseInvestigationManager;
